import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPut } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  AttributeRequest,
  AttributeResponse,
  ImportVariantCSVResponse,
  VariantImportMetadata,
  VariantManagementResponse,
  VariantRequest,
  VariantResponse,
} from './types';

export const getAttributes = (params: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<AttributeResponse[]>(
    generateNextPath(ENDPOINTS.ATTRIBUTES, params),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const getAttribute = (params: {
  accountId: string;
  projectId: string;
  attributeId: string;
}) => {
  return doGet<AttributeResponse>(
    generateNextPath(ENDPOINTS.ATTRIBUTE, params),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const putAttribute = ({
  body,
  ...params
}: {
  accountId: string;
  projectId: string;
  body: AttributeRequest;
}) => {
  return doPut<AttributeResponse>(
    generateNextPath(ENDPOINTS.ATTRIBUTES, params),
    LOGGER_TYPE.JUPITER,
    body,
    {
      version: 'v2',
    },
  );
};

export const deleteAttribute = (params: {
  accountId: string;
  projectId: string;
  attributeId: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.ATTRIBUTE, params),
    LOGGER_TYPE.JUPITER,
    {},
    {
      version: 'v2',
    },
  );
};

export const getVariants = (params: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<VariantResponse[]>(
    generateNextPath(ENDPOINTS.VARIANTS, params),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const getVariant = (params: {
  accountId: string;
  projectId: string;
  variantId: string;
}) => {
  return doGet<AttributeResponse>(
    generateNextPath(ENDPOINTS.VARIANT, params),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const putVariant = ({
  body,
  ...params
}: {
  accountId: string;
  projectId: string;
  body: VariantRequest;
}) => {
  return doPut<VariantResponse>(
    generateNextPath(ENDPOINTS.VARIANTS, params),
    LOGGER_TYPE.JUPITER,
    body,
    {
      version: 'v2',
    },
  );
};

export const deleteVariant = (params: {
  accountId: string;
  projectId: string;
  variantId: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.VARIANT, params),
    LOGGER_TYPE.JUPITER,
    {},
    {
      version: 'v2',
    },
  );
};

export const getVariantManagement = (params: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<VariantManagementResponse>(
    generateNextPath(ENDPOINTS.VARIANT_MANAGEMENT_VALUES, params),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const exportVariants = (accountId: string, projectId: string) => {
  return doGet<{ data: Blob; headers: Headers }>(
    generateNextPath(ENDPOINTS.VARIANTS_EXPORT, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2', downloadingFile: true, returnStatusCode: true },
  );
};

export const importVariants = (
  accountId: string,
  projectId: string,
  file: File,
) => {
  const url = generateNextPath(
    ENDPOINTS.VARIANTS_IMPORT_FILE,
    {
      accountId,
      projectId,
    },
    {
      type: 'csv',
      dry_run: 'true',
    },
  );
  const bodyFormData = new FormData();
  const blob = new Blob([file], { type: 'csv' });
  bodyFormData.append('csv', blob);

  return doPut<ImportVariantCSVResponse>(
    url,
    LOGGER_TYPE.JUPITER,
    bodyFormData,
    {
      postingFile: true,
      version: 'v2',
      parseResponseToJSON: true,
    },
  );
};

export const importVariantsDiff = ({
  accountId,
  projectId,
  diff,
}: {
  accountId: string;
  projectId: string;
  diff: VariantImportMetadata;
}) => {
  const url = generateNextPath(ENDPOINTS.VARIANTS_IMPORT, {
    accountId,
    projectId,
  });

  return doPut<ImportVariantCSVResponse>(url, LOGGER_TYPE.JUPITER, diff, {
    version: 'v2',
  });
};
